import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EMPTY_ENQUIRY, toEnquiryPayload, validateEnquiry } from "@/lib/enquiry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_ERROR =
  "We could not send your enquiry. Please try WhatsApp or email us directly.";

const readErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return FALLBACK_ERROR;
};

/**
 * Owns the enquiry form state, validation and submission so the form
 * component stays presentational.
 */
export const useEnquiryForm = (onSuccess) => {
  const [form, setForm] = useState(EMPTY_ENQUIRY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined, form: undefined }));
  }, []);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();

      const nextErrors = validateEnquiry(form);
      if (Object.keys(nextErrors).length) {
        setErrors(nextErrors);
        toast.error("Please check the highlighted fields.");
        return;
      }

      setSubmitting(true);
      try {
        const { data } = await axios.post(`${API}/contact`, toEnquiryPayload(form));
        if (!data?.success) throw new Error("Unexpected response");
        setForm(EMPTY_ENQUIRY);
        setErrors({});
        toast.success("Enquiry sent. We will get back to you shortly.");
        onSuccess();
      } catch (error) {
        toast.error(readErrorMessage(error));
        setErrors((prev) => ({ ...prev, form: "Submission failed. Please try again." }));
      } finally {
        setSubmitting(false);
      }
    },
    [form, onSuccess]
  );

  return { form, errors, submitting, setField, submit };
};
